// democracy.js
// Provides Massachusetts democracy issue configuration including stories, bills, and groups.
import avr from '../../assets/issue_images/ma/democracy/avr.jpg';
import modernization from '../../assets/issue_images/ma/democracy/modernization.jpg';
import votesact from '../../assets/issue_images/ma/democracy/votesact.jpg';
import youth from '../../assets/issue_images/ma/democracy/youth.jpg';

export const ISSUE_METADATA = {
  title: 'Strengthening Democracy in Massachusetts',
  summary:
    'Explore stories, bills, and advocacy groups working to strengthen democracy in Massachusetts.',
};

export const STORY_SCROLL_CONTENT = {
  topic: 'democracy',
  heroContent: {
    preGradientText: 'Everyday people are',
    gradientText: 'strengthening democracy',
    postGradientText: '',
    description: "Discover how Massachusetts civic groups are making government more transparent and representative."
},
  lastUpdated: 'November 2025',
};

export const BARRIER_STORY_CONTENT = [
  {
    key: 'avr-makes-difference',
    title: 'Automatic Voter Registration',
    caption:
      'A citizen-driven push expanded automatic registration to more than half a million residents in 2018.',
    details: [
      'A grassroots coalition (Common Cause MA, LWVMA, MassVOTE, MASSPIRG Students, allies) organized lobby days, constituent calls, and testimony; the Senate passed it unanimously and Gov. Charlie Baker signed it in Aug 2018.',
      'Advocates emphasized the scale of impact—Common Cause estimated ~680,000 eligible but unregistered residents—helping build momentum.',
      'Law took effect before 2020, simplifying access statewide and strengthening accurate rolls.',
    ],
    takeaway: "Grassroots pressure helped advance a reform that became law",
    imageSource: "lwvma.org",
    imageUrl: avr,
    sources: [
      'https://www.commoncause.org/massachusetts/press-releases/massachusetts-gov-charlie-baker-signs-automatic-voter-registration-into-law/',
      'https://www.brennancenter.org/our-work/analysis-opinion/automatic-voter-registration-now-law-massachusetts',
      'https://www.wamc.org/politics/2018-08-14/massachusetts-will-have-automatic-voter-registration-beginning-in-2020',
      'https://apnews.com/504834fe5c7d4812aad73a162a9a5f2d',
      'https://www.commoncause.org/massachusetts/press-releases/automatic-voter-registration-bill-passes-massachusetts-house/',
    ],
  },
  {
    key: 'first-step-online-reg',
    title: 'Election Modernization Act (2014)',
    caption:
      'After years pushing, community groups advanced a law that made online registration, early voting, and pre-registration for 16-17-year-olds easier.',
    details: [
      'The Election Modernization Coalition coordinated volunteer toolkits, scripts, and local action plans; residents met legislators and wrote op-eds to press for change.',
      'Gov. Deval Patrick signed the bill in May 2014, adding online voter registration, up to 10–11 days of early voting, and pre-registration for 16–17-year-olds.',
      'Advocates then helped towns implement the new options before the 2016 election—turning a statewide win into practical, local access.',
      'Result: a clearer on-ramp—anyone with internet access could register or check status in minutes, and teens could get ready to vote.',
    ],
    takeaway: "Years of steady organizing made voting simpler for everyone",
    imageUrl: modernization,
    imageSource: 'pirg.org',
    sources: [
      'https://lwvma.org/wp-content/uploads/2016/01/Complete-Volunteer-Toolkit-FINAL.pdf',
      'https://www.wgbh.org/news/local/2014-05-22/patrick-signs-early-voting-into-law',
      'https://baystatebanner.com/2014/05/28/election-reform-legislation-modernizes-massachusetts-voting/',
      'https://www.commoncause.org/massachusetts/work/election-modernization/',
      'https://www.wcvb.com/article/massachusetts-governor-signs-early-voting-bill/8201847',
    ],
  },
  {
    key: 'youth-are-politics-too',
    title: 'Youth Pre-Registration & Organizing (2016–2019)',
    caption:
      'After pre-registration became law, groups registered tens of thousands of 16-17-year-olds, doubling youth turnout in the 2018 midterms.',
    details: [
      'Massachusetts implemented pre-registration for 16–17-year-olds (from the 2014 law) in 2016; youth, schools, and nonprofits ran drives in classrooms and community centers.',
      "By 2018, over 42,000 teens had pre-registered; between 2016–2018, more than 62,000 young people were added as 'future voters.'",
      'Youth turnout (ages 18–29) in MA reached ~33% in 2018—about double 2014—after widespread peer-to-peer outreach and registration efforts.',
      'The Massachusetts Voter Table trained 1,000+ grassroots leaders and contacted 1,000,000+ voters (2011–2024), with strong focus on Gateway Cities—showing “politics” includes students, immigrant families, and working-class communities.',
    ],
    takeaway: "Young organizers showed that politics includes everyone",
    imageUrl: youth,
    imageSource: "masspirgstudents.org",
    sources: [
      'https://www.acslaw.org/wp-content/uploads/2018/05/Registration-Massachusetts.pdf',
      'https://thecivicscenter.org/blog/2019/8/14/massachusetts-pre-registration-rate-rises-to-25',
      'https://www.massvote.org/history-and-achievements',
      'https://mavotertable.org/mvt-collective-impact',
      'https://www.commoncause.org/massachusetts/work/election-modernization/',
    ],
  },
  {
    key: 'no-time-votes-act',
    title: 'The VOTES Act (2022)',
    caption:
      'A coalition of everyday voters, clerks, and advocates advanced permanent no-excuse vote-by-mail and expanded early voting',
    details: [
      'After residents used temporary 2020 options at record levels, advocates (ACLU MA, LWVMA, Common Cause MA, MassVOTE) mobilized stories, calls, and testimonies to make them permanent.',
      'Gov. Baker signed the VOTES Act in June 2022: no-excuse mail voting, more early voting, improved access for eligible voters in jails, and cleaner rolls (ERIC).',
      'Impact: Massachusetts set participation records in 2020 (3.46–3.7M ballots; majority early/mail), proving convenience boosts turnout; the VOTES Act locked in those time-saving options.',
      'Clear next step for residents: request a mail ballot or vote early—no special excuse needed.',
    ],
    takeaway: 'Advocacy groups made it simple for people to get involved',
    imageUrl: votesact,
    imageSource: 'aclum.org',
    sources: [
      'https://www.aclum.org/sites/default/files/field_documents/votesact_factsheet.pdf',
      'https://www.commoncause.org/massachusetts/work/the-votes-act/',
      'https://www.axios.com/local/boston/2022/06/22/massachusetts-governor-makes-mail-in-voting-permanent',
      'https://lwvma.org/vote-by-mail/',
      'https://pirg.org/massachusetts/media-center/massachusetts-elections-were-a-success-advocates-applaud-election-officials/',
    ],
  },
];


export const BILLS = [
  {
    id: "same-day-registration",
    title: "Let voters register and vote on the same day",
    numbers: {
      house: { label: "H.834", url: "https://malegislature.gov/Bills/194/H834" },
      senate: { label: "S.505", url: "https://malegislature.gov/Bills/194/S505" },
    },
    short_summary: "Enables same-day voter registration statewide.",
    short_summary_gerund: "Enabling same-day voter registration statewide.",
    why: "23 other states already allow same-day registration; it's a proven reform that can increase voter turnout by up to 7 percentage points and ensure no qualified voter is turned away.",
    bullets: [
      "Lets eligible residents register and vote on the same day, including on Election Day and during early voting.",
      "Eliminates the arbitrary 10-day advance registration rule that currently prevents thousands from voting in each election.",
      "Follows 22+ other states where same-day registration has significantly boosted voter participation."
    ],
    supporters: [
      { name: "Common Cause Massachusetts", joinUrl: "https://www.commoncause.org/massachusetts/take-action", infoUrl: "https://www.commoncause.org/massachusetts" },
      { name: "MassVOTE", joinUrl: "https://www.massvote.org/volunteer", infoUrl: "https://www.massvote.org" },
      { name: "League of Women Voters of Massachusetts", joinUrl: "https://lwvma.org/get-involved", infoUrl: "https://lwvma.org" },
      { name: "ACLU of Massachusetts", joinUrl: "https://www.aclum.org/en/take-action/volunteer", infoUrl: "https://www.aclum.org" },
      { name: "Massachusetts Voter Table", joinUrl: "https://mavotertable.org/contact-us", infoUrl: "https://mavotertable.org" }
    ]
  },
  {
    id: "census-voter-decoupling",
    title: "Keep the town census from disrupting voter registration",
    numbers: {
      house: { label: "H.799", url: "https://malegislature.gov/Bills/194/H799" },
      senate: { label: "S.503", url: "https://malegislature.gov/Bills/194/S503" },
    },
    short_summary: "Stops local censuses from triggering voter purges.",
    short_summary_gerund: "Stopping censuses from triggering voter purges.",
    why: "Protects the voting rights of hundreds of thousands of Massachusetts voters each year by ending a bureaucratic practice that could disenfranchise them for not mailing back a census form.",
    bullets: [
      "Stops treating failure to return the annual municipal census as a reason to change a voter's status to \"inactive\".",
      "Keeps the local census for data and jury selection, but decouples it from any voter registration status changes.",
      "Prevents roughly 700,000 voters every year from facing needless hurdles at the polls due to a paperwork technicality."
    ],
    supporters: [
      { name: "Common Cause Massachusetts", joinUrl: "https://www.commoncause.org/massachusetts/take-action", infoUrl: "https://www.commoncause.org/massachusetts" },
      { name: "MassVOTE", joinUrl: "https://www.massvote.org/volunteer", infoUrl: "https://www.massvote.org" },
      { name: "League of Women Voters of Massachusetts", joinUrl: "https://lwvma.org/get-involved", infoUrl: "https://lwvma.org" },
      { name: "ACLU of Massachusetts", joinUrl: "https://www.aclum.org/en/take-action/volunteer", infoUrl: "https://www.aclum.org" },
      { name: "Massachusetts Voter Table", joinUrl: "https://mavotertable.org/contact-us", infoUrl: "https://mavotertable.org" }
    ]
  },
  {
    id: "voting-accessibility",
    title: "Make every polling place accessible for disabled voters",
    numbers: {
      house: { label: "H.820", url: "https://malegislature.gov/Bills/194/H820" },
      senate: { label: "S.504", url: "https://malegislature.gov/Bills/194/S504" },
    },
    short_summary: "Mandates accessibility inspections at every voting site.",
    short_summary_gerund: "Mandating accessibility inspections at voting sites.",
    why: "Ensures voters with disabilities can cast their ballots in person without barriers by mandating compliance and empowering authorities to enforce accessibility laws statewide.",
    bullets: [
      "Mandates the Secretary of the Commonwealth to have all polling places and early voting sites inspected at least once every four years for ADA accessibility.",
      "If a city or town is found non-compliant with accessibility laws, it must take immediate corrective action and submit a compliance plan.",
      "Authorizes the Attorney General to enforce fixes in court if necessary, holding localities accountable for accessible voting locations."
    ],
    supporters: [
      { name: "Common Cause Massachusetts", joinUrl: "https://www.commoncause.org/massachusetts/take-action", infoUrl: "https://www.commoncause.org/massachusetts" },
      { name: "MassVOTE", joinUrl: "https://www.massvote.org/volunteer", infoUrl: "https://www.massvote.org" },
      { name: "League of Women Voters of Massachusetts", joinUrl: "https://lwvma.org/get-involved", infoUrl: "https://lwvma.org" },
      { name: "ACLU of Massachusetts", joinUrl: "https://www.aclum.org/en/take-action/volunteer", infoUrl: "https://www.aclum.org" }
    ]
  },
  {
    id: "voting-rights-restoration",
    title: "Restore voting rights for people in prison",
    numbers: {
      house: { label: "H.873", url: "https://malegislature.gov/Bills/194/H873" },
      senate: { label: "S.524", url: "https://malegislature.gov/Bills/194/S524" },
    },
    short_summary: "Restores voting rights for people in prison.",
    short_summary_gerund: "Restoring voting rights for people in prison.",
    why: "Would end a policy that currently disenfranchises over 7,300 citizens — disproportionately Black and Latino — and affirm that incarceration should not cost anyone their voice in democracy.",
    bullets: [
      "Repeals the 20-year-old ban that prevents people serving a felony prison sentence from voting.",
      "Ensures no one loses their right to vote due to a felony conviction or incarceration.",
      "Advances racial justice, as felony disenfranchisement disproportionately impacts Black and Latinx communities."
    ],
    supporters: [
      { name: "Neighbor to Neighbor Massachusetts", joinUrl: "https://n2nma.org/take-action", infoUrl: "https://n2nma.org" }
    ]
  },
  {
    id: "local-rcv",
    title: "Let cities choose ranked choice voting for local races",
    numbers: {
      senate: { label: "S.531", url: "https://malegislature.gov/Bills/194/S531" },
    },
    short_summary: "Lets cities adopt local ranked choice voting.",
    short_summary_gerund: "Allowing cities to adopt local ranked choice voting.",
    why: "Gives communities more power to shape their local democracy by using RCV, which can foster more representative elections and greater voter engagement at the municipal level.",
    bullets: [
      "Empowers any municipality to implement Ranked Choice Voting for local offices on its own initiative.",
      "Removes the requirement for separate state legislation or home-rule petitions before a city or town can use RCV, streamlining local adoption.",
      "Aims to increase voter choice and encourage more positive, issue-focused campaigns by allowing ranking of candidates rather than choosing just one."
    ],
    supporters: [
      { name: "Voter Choice Massachusetts", joinUrl: "https://vcma.nationbuilder.com", infoUrl: "https://voterchoicema.org" }
    ]
  },
  {
    id: "open-meeting-access",
    title: "Keep hybrid access for every public meeting",
    numbers: {
      house: { label: "H.3299", url: "https://malegislature.gov/Bills/194/H3299" },
    },
    short_summary: "Requires hybrid access for all public meetings.",
    short_summary_gerund: "Requiring hybrid access for all public meetings.",
    why: "Makes civic participation more inclusive by ensuring people with disabilities, lack of transport, or schedule conflicts can attend and testify at public meetings remotely, so \"access to public meetings should never again hinge\" on physical presence.",
    bullets: [
      "Makes permanent the pandemic-era practice of hybrid meetings: members of the public (and officials) can attend hearings and meetings either in person or via video/phone.",
      "Ensures that a person’s ability to engage with government meetings isn’t limited by mobility, geography, work schedule, or caregiving responsibilities.",
      "Modernizes the Open Meeting Law to leverage technology for greater transparency and public access to local and state meetings."
    ],
    supporters: [
      { name: "ACLU of Massachusetts", joinUrl: "https://www.aclum.org/en/take-action/volunteer", infoUrl: "https://www.aclum.org" },
      { name: "Common Cause Massachusetts", joinUrl: "https://www.commoncause.org/massachusetts/take-action", infoUrl: "https://www.commoncause.org/massachusetts" }
    ]
  },
  {
    id: "legislative-transparency",
    title: "Shine more light on State House decisions",
    numbers: {
      senate: { label: "S.2099", url: "https://malegislature.gov/Bills/194/S2099" },
    },
    short_summary: "Publishes committee votes and extends hearing notice.",
    short_summary_gerund: "Publishing committee votes and extending hearing notice.",
    why: "Increases transparency and accountability on Beacon Hill, making it easier for citizens to track how their lawmakers vote and engage with legislation before decisions are made.",
    bullets: [
      "Requires all legislative committee votes to be posted publicly on the Legislature’s website within 48 hours of the vote.",
      "Mandates at least two weeks’ advance notice for committee hearings, so the public has fair warning to participate or submit testimony.",
      "Ensures testimony from hearings is available (with privacy protections) on request, so the legislative record is more open.",
      "Ends Massachusetts’ unique exemption of the Governor’s office from public records law by subjecting the executive branch to disclosure requirements."
    ],
    supporters: [
      { name: "Act On Mass", joinUrl: "https://actonmass.org/the-campaign", infoUrl: "https://actonmass.org" }
    ]
  },
  {
    id: "stipend-reform",
    title: "Tie leadership stipends to getting the work done",
    numbers: {
      senate: { label: "SD.1301", url: "https://malegislature.gov/Bills/194/SD1301" },
      house: { label: "HD.4303", url: "https://malegislature.gov/Bills/194/HD4303" },
    },
    short_summary: "Cuts leadership stipends and ties them to work.",
    short_summary_gerund: "Cutting leadership stipends and tying them to work.",
    why: "Curtails a major source of top-down control in the State House – the Speaker’s and Senate President’s ability to reward or punish lawmakers with stipends – to foster a more independent, democratic legislature.",
    bullets: [
      "Drastically reduces the number of leadership stipends controlled by House/Senate leadership (Massachusetts currently hands out far more stipends than most states).",
      "Adds a pay increase for rank-and-file legislators to narrow the gap between those with leadership stipends and those without.",
      "Ties remaining stipends to performance and transparency benchmarks, ensuring committee chairs only get extra pay if they run efficient, open committees.",
      "Limits a key tool of patronage so that legislative leaders can’t easily use paychecks to demand loyalty from rank-and-file members."
    ],
    supporters: [
      { name: "Act On Mass", joinUrl: "https://actonmass.org/the-campaign", infoUrl: "https://actonmass.org" }
    ]
  },
  {
    id: "staff-unionization",
    title: "Give State House staff the right to unionize",
    numbers: {
      senate: { label: "S.1343", url: "https://malegislature.gov/Bills/194/S1343" },
      house: { label: "H.2970", url: "https://malegislature.gov/Bills/194/H2970" },
    },
    short_summary: "Extends collective bargaining rights to State House staff.",
    short_summary_gerund: "Extending collective bargaining rights to State House staff.",
    why: "Makes the State House a fairer workplace by addressing low pay, long hours, and inequities among legislative staff – ultimately empowering staff and the lawmakers they serve through better working conditions.",
    bullets: [
      "Allows employees of the Massachusetts Legislature (both House and Senate staff) to form and join a labor union for the first time.",
      "Lets legislative staff collectively negotiate for improved wages, benefits, and working conditions, like other public employees can.",
      "Seeks to remedy issues like underpayment, discrimination, and overwork among staff by giving them a voice at the bargaining table.",
      "Reduces leadership’s tight control over staffing and resources – many rank-and-file lawmakers currently have minimal staff support – by empowering staff and encouraging a more equitable distribution of resources."
    ],
    supporters: [
      { name: "Act On Mass", joinUrl: "https://actonmass.org/the-campaign", infoUrl: "https://actonmass.org" }
    ]
  },
];

export const GROUPS = [
  {
    name: "Common Cause Massachusetts",
    slug: "common-cause-massachusetts",
    summary: "Advocates for transparent, accountable elections statewide.",
    joinUrl: "https://www.commoncause.org/massachusetts/take-action",
    logoUrl: "https://placehold.co/64x64?text=Logo",
    supports: ["same-day-registration", "census-voter-decoupling", "voting-accessibility", "open-meeting-access"]
  },
  {
    name: "MassVOTE",
    slug: "massvote",
    summary: "Boosts civic participation through voter education and advocacy.",
    joinUrl: "https://www.massvote.org/volunteer",
    logoUrl: "https://placehold.co/64x64?text=Logo",
    supports: ["same-day-registration", "census-voter-decoupling", "voting-accessibility"]
  },
  {
    name: "League of Women Voters of Massachusetts",
    slug: "league-of-women-voters-of-massachusetts",
    summary: "Nonpartisan coalition expanding voting access and transparency.",
    joinUrl: "https://lwvma.org/get-involved",
    logoUrl: "https://placehold.co/64x64?text=Logo",
    supports: ["same-day-registration", "census-voter-decoupling", "voting-accessibility"]
  },
  {
    name: "Act On Mass",
    slug: "act-on-mass",
    summary: "Pushes for transparent, democratic Massachusetts legislative rules.",
    joinUrl: "https://actonmass.org/the-campaign",
    logoUrl: "https://placehold.co/64x64?text=Logo",
    supports: ["legislative-transparency", "stipend-reform", "staff-unionization"]
  },
  {
    name: "Voter Choice Massachusetts",
    slug: "voter-choice-massachusetts",
    summary: "Promotes ranked choice voting across Massachusetts communities.",
    joinUrl: "https://vcma.nationbuilder.com",
    logoUrl: "https://placehold.co/64x64?text=Logo",
    supports: ["local-rcv"]
  },
  {
    name: "Massachusetts Voter Table",
    slug: "massachusetts-voter-table",
    summary: "Coalition building voter power in marginalized communities.",
    joinUrl: "https://mavotertable.org/contact-us",
    logoUrl: "https://placehold.co/64x64?text=Logo",
    supports: ["same-day-registration", "census-voter-decoupling"]
  },
  {
    name: "Neighbor to Neighbor Massachusetts",
    slug: "neighbor-to-neighbor-massachusetts",
    summary: "Organizes working-class communities for racial and economic justice.",
    joinUrl: "https://n2nma.org/take-action",
    logoUrl: "https://placehold.co/64x64?text=Logo",
    supports: ["voting-rights-restoration"]
  },
  {
    name: "ACLU of Massachusetts",
    slug: "aclu-of-massachusetts",
    summary: "Defends civil liberties and expands democratic participation.",
    joinUrl: "https://www.aclum.org/en/take-action/volunteer",
    logoUrl: "https://placehold.co/64x64?text=Logo",
    supports: ["same-day-registration", "census-voter-decoupling", "voting-accessibility", "open-meeting-access"]
  }
];
